import { BaseClient, normalizeQuery, type Paged } from '../base-client';
import type { ExportCustomersQuery } from '../customers/types';
import type {
  CreateCustomerTeamMemberInput,
  CustomerTeamMember,
  CustomerTeamMemberWithLabels,
  EndCustomerTeamMemberInput,
  ImportTeamResult,
  PatchCustomerTeamMemberInput,
} from './types';

type One = { data: CustomerTeamMember };
type Many = { data: CustomerTeamMember[] };
type OneWithLabels = { data: CustomerTeamMemberWithLabels };
type ManyWithLabels = { data: CustomerTeamMemberWithLabels[] };
type PagedWithLabels = Paged<CustomerTeamMemberWithLabels>;
type ImportResponse = { data: ImportTeamResult };

/** Pagination + name/email search for the team list endpoints. */
export interface TeamListQuery {
  asOf?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

function teamQuery(opts: TeamListQuery): Record<string, string | number> {
  const query: Record<string, string | number> = {};
  if (opts.asOf) query.asOf = opts.asOf;
  if (opts.q) query.q = opts.q;
  if (opts.limit != null) query.limit = opts.limit;
  if (opts.offset != null) query.offset = opts.offset;
  return query;
}

export class CustomerTeamClient extends BaseClient {
  /**
   * Memberships in effect on `asOf` (defaults to today). The standard
   * "current team" listing — drives the customer-detail Team tab. Enriched
   * with member + service-role names. Paginated; `q` searches by name/email.
   */
  listInEffect(customerId: string, opts: TeamListQuery = {}): Promise<PagedWithLabels> {
    return this.request(`/api/customers/${encodeURIComponent(customerId)}/team`, {
      query: teamQuery(opts),
    });
  }

  /** Every membership for a customer — past, present, future. Enriched.
   *  Paginated; `q` searches by name/email (`asOf` is ignored here). */
  listAll(customerId: string, opts: TeamListQuery = {}): Promise<PagedWithLabels> {
    return this.request(`/api/customers/${encodeURIComponent(customerId)}/team/all`, {
      query: teamQuery(opts),
    });
  }

  /**
   * Inverse list: memberships for `teamUserId` whose effective range
   * overlaps `[fromDate, toDate]`. Used by the timesheet web app to
   * scope the customer dropdown and the per-day "on team" map to the
   * displayed user + week — it reads only ids, so this returns BARE rows
   * (no per-request label resolution). Use `listForUserWithLabels` when you
   * need `userName`/`serviceRoleName`.
   */
  listForUser(params: { teamUserId: string; fromDate: string; toDate: string }): Promise<Many> {
    return this.request('/api/customer-team', { query: params });
  }

  /** Same inverse list, enriched with display labels — for the user-detail
   *  customer section. Opts into the server-side label resolution the bare
   *  `listForUser` skips. */
  listForUserWithLabels(params: {
    teamUserId: string;
    fromDate: string;
    toDate: string;
  }): Promise<ManyWithLabels> {
    return this.request('/api/customer-team', { query: { ...params, withLabels: 'true' } });
  }

  get(id: string): Promise<OneWithLabels> {
    return this.request(`/api/customer-team/${encodeURIComponent(id)}`);
  }

  create(customerId: string, input: CreateCustomerTeamMemberInput): Promise<One> {
    return this.request(`/api/customers/${encodeURIComponent(customerId)}/team`, {
      method: 'POST',
      body: input,
    });
  }

  patch(id: string, input: PatchCustomerTeamMemberInput): Promise<One> {
    return this.request(`/api/customer-team/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  /** Soft delete — sets `effectiveTo`. `effectiveTo` is required so the
   *  server (UTC) doesn't have to guess the caller's local "today". */
  end(id: string, input: EndCustomerTeamMemberInput): Promise<One> {
    return this.request(`/api/customer-team/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: input,
    });
  }

  /** Reopen an ended membership by clearing `effectiveTo`. */
  restore(id: string): Promise<One> {
    return this.request(`/api/customer-team/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }

  /**
   * Download the tenant's current team members as an Excel (.xlsx) blob —
   * members in effect today. Each row carries the customer's Client ID and the
   * member's email + name; only email is used on import. Gated server-side on
   * `customer.team.view`. `customerFilter` mirrors the customers list
   * filter (status / search / custom fields) so the export covers only
   * memberships of the customers visible on screen; pass `{}` to export all.
   */
  exportFile(
    customerFilter: ExportCustomersQuery = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/customer-team/export', {
      query: normalizeQuery(customerFilter),
    });
  }

  /** Download a blank Excel (.xlsx) template with only the import header row. */
  exportTemplate(): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/customer-team/import/template');
  }

  /**
   * Upload an .xlsx or .csv file. The file is the COMPLETE team per
   * customer: members absent from the file are ended (today), members
   * present are upserted by email. Best-effort — valid rows persist,
   * invalid rows come back with per-row errors. Pass `idempotencyKey`
   * to make the POST retry-safe.
   */
  importFile(file: File, opts: { idempotencyKey?: string } = {}): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.requestMultipart('/api/customer-team/import', fd, { headers });
  }
}
